import React, { useState, useEffect } from 'react';
import { useAdmin } from '../../context/AdminContext';
import { imagesAPI } from '../../services/api';
import '../admin/AdminComponents.css';

import Alys_80b from '../../assets/images/sides/Alys_80b.png';
import Bianca97 from '../../assets/images/sides/Bianca97.png';
import Bianca111b from '../../assets/images/sides/Bianca111b.png';
import Kris_36c from '../../assets/images/sides/Kris_36c.png';
import Kris_33a from '../../assets/images/sides/Kris_33a.png';
import Rico_1b from '../../assets/images/sides/Rico_1b.png';

const defaultSideImages = {
  Alys_80b,
  Bianca97,
  Bianca111b,
  Kris_36c,
  Kris_33a,
  Rico_1b
};

const brands = ['veloura', 'vague'];

const getSectionsForBrand = (brand) => {
  if (brand === 'vague') {
    return [
      { value: 'about', label: 'About Vague' },
      { value: 'studios', label: 'Collections' },
      { value: 'vague', label: 'Culture' },
      { value: 'opportunities', label: 'Shop' }
    ];
  }

  return [
    { value: 'about', label: 'About Veloura' },
    { value: 'studios', label: 'Veloura Studios' },
    { value: 'vague', label: 'Vague' },
    { value: 'careers', label: 'Careers' },
    { value: 'partners', label: 'Partners' },
    { value: 'news', label: 'News' }
  ];
};

const ImagesManagement = () => {
  const {
    sectionImages,
    updateSectionImage,
    sideImages,
    resetToDefaults
  } = useAdmin();

  const [selectedBrand, setSelectedBrand] = useState('veloura');
  const [selectedSection, setSelectedSection] = useState('about');
  const [availableImages, setAvailableImages] = useState([]);
  const [draggedImage, setDraggedImage] = useState(null);
  const [dragOverSide, setDragOverSide] = useState(null);
  const [uploading, setUploading] = useState(false);

  const sections = getSectionsForBrand(selectedBrand);

  const defaultImagesToShow =
    sideImages && Object.keys(sideImages).length > 0
      ? sideImages
      : defaultSideImages;

  useEffect(() => {
    const stored = localStorage.getItem('veloura_available_images');

    if (stored) {
      try {
        const parsedImages = JSON.parse(stored);

        if (Array.isArray(parsedImages)) {
          setAvailableImages(parsedImages);
        }
      } catch (error) {
        console.error('Error loading available images:', error);
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('veloura_available_images', JSON.stringify(availableImages));
  }, [availableImages]);

  useEffect(() => {
    setSelectedSection('about');
  }, [selectedBrand]);

  const handleImageChange = async (brand, section, side, value) => {
    try {
      await updateSectionImage(brand, section, side, value);
    } catch (error) {
      alert(error.message || 'Failed to update image');
    }
  };

  const handleImageUpload = async (brand, section, side, file) => {
    if (!file) return null;

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return null;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB');
      return null;
    }

    try {
      const response = await imagesAPI.uploadLandingImage(file);

      if (response.success && response.data) {
        const imageUrl = response.data.url || response.data.path;

        if (brand && section && side) {
          await handleImageChange(brand, section, side, imageUrl);
        }

        return imageUrl;
      }

      alert(response.message || 'Error uploading image');
      return null;
    } catch (error) {
      console.error('Upload error:', error);
      alert(error.message || 'Error uploading image');
      return null;
    }
  };

  const handleUploadToAvailable = async (file) => {
    if (!file) return;

    setUploading(true);

    try {
      const imageUrl = await handleImageUpload(null, null, null, file);

      if (imageUrl) {
        const imageName = file.name || `image_${Date.now()}`;

        setAvailableImages((prev) => [
          ...prev,
          {
            id: `${Date.now()}-${imageName}`,
            name: imageName,
            url: imageUrl
          }
        ]);
      }
    } catch (error) {
      console.error('Error uploading to available:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleDragStart = (event, imageUrl) => {
    setDraggedImage(imageUrl);
    event.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => {
    setDraggedImage(null);
    setDragOverSide(null);
  };

  const handleDragOver = (event, side) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    setDragOverSide(side);
  };

  const handleDragLeave = () => {
    setDragOverSide(null);
  };

  const handleDrop = async (event, brand, section, side) => {
    event.preventDefault();
    setDragOverSide(null);

    if (draggedImage) {
      await handleImageChange(brand, section, side, draggedImage);
      setDraggedImage(null);
    }
  };

  const handleDeleteAvailableImage = (id) => {
    if (window.confirm('Are you sure you want to remove this image from available images?')) {
      setAvailableImages((prev) => prev.filter((image) => image.id !== id));
    }
  };

  const currentSection =
    sectionImages &&
    sectionImages[selectedBrand] &&
    sectionImages[selectedBrand][selectedSection]
      ? sectionImages[selectedBrand][selectedSection]
      : { left: '', right: '' };

  return (
    <div className="admin-section">
      <div className="admin-section-header">
        <h2 className="admin-section-title">Landing Page Images</h2>

        <div className="admin-section-actions">
          <button
            className="admin-button danger"
            onClick={async () => {
              if (window.confirm('Are you sure you want to reset all images to defaults?')) {
                try {
                  await resetToDefaults();
                } catch (error) {
                  alert(error.message || 'Failed to reset images');
                }
              }
            }}
          >
            Reset to Defaults
          </button>
        </div>
      </div>

      <div className="admin-images-controls">
        <div className="admin-form-group">
          <label className="admin-form-label">Brand</label>

          <select
            className="admin-form-select"
            value={selectedBrand}
            onChange={(event) => setSelectedBrand(event.target.value)}
            style={{ width: 'auto', minWidth: '200px' }}
          >
            {brands.map((brand) => (
              <option key={brand} value={brand}>
                {brand.charAt(0).toUpperCase() + brand.slice(1)}
              </option>
            ))}
          </select>
        </div>

        <div className="admin-form-group">
          <label className="admin-form-label">Section</label>

          <select
            className="admin-form-select"
            value={selectedSection}
            onChange={(event) => setSelectedSection(event.target.value)}
            style={{ width: 'auto', minWidth: '200px' }}
          >
            {sections.map((section) => (
              <option key={section.value} value={section.value}>
                {section.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="admin-images-grid">
        <div
          className={`admin-image-section admin-drop-zone ${dragOverSide === 'left' ? 'drag-over' : ''}`}
          onDragOver={(event) => handleDragOver(event, 'left')}
          onDragLeave={handleDragLeave}
          onDrop={(event) => handleDrop(event, selectedBrand, selectedSection, 'left')}
        >
          <label className="admin-form-label">Left Image</label>

          {currentSection.left ? (
            <div className="admin-image-preview-container">
              <img
                src={currentSection.left}
                alt="Left preview"
                className="admin-image-preview"
                onError={(event) => {
                  event.target.style.display = 'none';
                }}
              />
            </div>
          ) : (
            <div className="admin-drop-zone-placeholder">
              Drop image here from available images
            </div>
          )}
        </div>

        <div
          className={`admin-image-section admin-drop-zone ${dragOverSide === 'right' ? 'drag-over' : ''}`}
          onDragOver={(event) => handleDragOver(event, 'right')}
          onDragLeave={handleDragLeave}
          onDrop={(event) => handleDrop(event, selectedBrand, selectedSection, 'right')}
        >
          <label className="admin-form-label">Right Image</label>

          {currentSection.right ? (
            <div className="admin-image-preview-container">
              <img
                src={currentSection.right}
                alt="Right preview"
                className="admin-image-preview"
                onError={(event) => {
                  event.target.style.display = 'none';
                }}
              />
            </div>
          ) : (
            <div className="admin-drop-zone-placeholder">
              Drop image here from available images
            </div>
          )}
        </div>
      </div>

      <div className="admin-available-images">
        <h3 className="admin-subsection-title">Available Images</h3>
        <p className="admin-hint">Drag and drop images to left/right slots</p>

        <div className="admin-upload-area">
          <input
            type="file"
            accept="image/*"
            id="available-image-upload"
            style={{ display: 'none' }}
            onChange={(event) => {
              handleUploadToAvailable(event.target.files[0]);
              event.target.value = '';
            }}
            disabled={uploading}
          />

          <label
            htmlFor="available-image-upload"
            className="admin-upload-button"
            style={{
              display: 'inline-block',
              padding: '1rem 2rem',
              background: 'var(--accent)',
              color: 'var(--text-primary)',
              border: 'none',
              borderRadius: '4px',
              cursor: uploading ? 'not-allowed' : 'pointer',
              opacity: uploading ? 0.6 : 1,
              marginBottom: '1rem',
              fontFamily: 'Space Grotesk, sans-serif',
              fontSize: '0.9rem',
              fontWeight: 600
            }}
          >
            {uploading ? 'Uploading...' : '+ Upload Image to Available'}
          </label>
        </div>

        <div className="admin-images-grid-small">
          {Object.entries(defaultImagesToShow).map(([key, value]) => (
            <div
              key={`default-${key}`}
              className="admin-image-thumbnail draggable"
              draggable
              onDragStart={(event) => handleDragStart(event, value)}
              onDragEnd={handleDragEnd}
              title="Drag to left/right slot"
            >
              <img src={value} alt={key} />
              <span>{key}</span>
            </div>
          ))}

          {availableImages.map((image) => (
            <div
              key={image.id}
              className="admin-image-thumbnail draggable"
              draggable
              onDragStart={(event) => handleDragStart(event, image.url)}
              onDragEnd={handleDragEnd}
              title="Drag to left/right slot"
            >
              <img src={image.url} alt={image.name} />

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '0.5rem'
                }}
              >
                <span style={{ flex: 1, textAlign: 'center' }}>
                  {image.name}
                </span>

                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleDeleteAvailableImage(image.id);
                  }}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: 'var(--text-secondary)',
                    cursor: 'pointer',
                    padding: '0.25rem 0.5rem',
                    fontSize: '1.2rem',
                    lineHeight: 1
                  }}
                  title="Remove from available images"
                >
                  ×
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ImagesManagement;
